// ^ necessary because block needs to be any

import React, { lazy } from 'react';
import get from 'lodash.get';
import {
    isTypeBlockAccordionContent,
    isTypeBlockClientTestimonial,
    isTypeBlockDarkCircleBackground,
    isTypeBlockFeaturedCaseStudies,
    isTypeBlockIconBlocks,
    isTypeBlockImageWithPartialOverlay,
    isTypeBlockIndustryBlocks,
    isTypeBlockJobList,
    isTypeBlockLogoCarousel,
    isTypeBlockLogoGrid,
    isTypeBlockNumberedBlocks,
    isTypeBlockPeopleList,
    isTypeBlockRichText,
    isTypeBlockScrolljackSection,
    isTypeBlockSlideshow,
    isTypeBlockStackedCards,
    isTypeBlockTabbedContent,
    isTypeBlockTechnologyBlocks,
    isTypeCtaSimple,
    isTypeForm,
    isTypeBlockGradientCta,
} from '@fm-nx/contentful-types';
import { FadeOnScroll } from '../fade-on-scroll/fade-on-scroll';
import { transformFactToGraphQL } from '../__transformers__/transform-fact-to-graphql';
import { transformImageWrapperToGraphQL } from '../__transformers__/transform-image-wrapper-to-graphql';
const AccordionContent = lazy(() => import('../accordion-content/accordion-content'));
const NumberedBlocks = lazy(() => import('../numbered-blocks/numbered-blocks'));
const BlockImageWithPartialOverlay = lazy(() => import('../image-with-partial-overlay/image-with-partial-overlay'));
const PageSection = lazy(() => import('../page-section/page-section'));
const IconBlocks = lazy(() => import('../icon-blocks/icon-blocks'));
const IndustryBlocks = lazy(() => import('../industry-blocks/industry-blocks'));
const TechnologyBlocks = lazy(() => import('../technology-blocks/technology-blocks'));
const CtaFullSimple = lazy(() => import('../cta-full-simple/cta-full-simple'));
const ClientTestimonial = lazy(() => import('../client-testimonial/client-testimonial'));
const BlockDarkSection = lazy(() => import('../block-dark-section/block-dark-section'));
const PeopleList = lazy(() => import('../people-list/people-list'));
const Form = lazy(() => import('../form/form'));
const FeaturedCaseStudyList = lazy(() => import('../featured-case-study-list/featured-case-study-list'));
const ClientLogoGrid = lazy(() => import('../client-logo-grid/client-logo-grid'));
const BlockLogoCarousel = lazy(() => import('../block-logo-carousel/block-logo-carousel'));
const JobList = lazy(() => import('../job-list/job-list'));
const BlockImage = lazy(() => import('../block-image/block-image'));
const TabbedContent = lazy(() => import('../tabbed-content/tabbed-content'));
const StackedCards = lazy(() => import('../stacked-cards/stacked-cards-parallax'));
const ScrolljackSection = lazy(() => import('../scrolljack-section/scrolljack-section'));
const BlockSlideshow = lazy(() => import('../block-slideshow/block-slideshow'));
const BlockTestimonialSlideShow = lazy(() => import('../block-testimonial-slide-show/block-testimonial-slide-show'));
const TextRichCentered = lazy(() => import('../text-rich-centered/text-rich-centered'));
const BlockGradientCta = lazy(() => import('../block-gradient-cta/block-gradient-cta'));

type BlockRendererProps = {
    block: any;
    mode?: 'dark' | 'light';
    index?: number;
    // used for block-dark-section that nests block renderer in component
    // used to prevent non-unique ids for anchor links
    isNested?: boolean;
    context?: 'default' | 'dark-circle';
    suppressFirstBlockTopMargin?: boolean;
};

function slugify(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/[\s]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

function getBlockAnchorId(block: any, index?: number, isNested?: boolean): string | undefined {
    const overline = block?.fields?.overline;
    if (overline && typeof overline === 'string') {
        return slugify(overline);
    }
    // Nested blocks without an overline get no ID (avoids index conflicts with top-level)
    if (isNested) return undefined;
    return `block-${index ?? 0}`;
}

// Map Contentful content type ids from content models to components
const ContentTypeMap = {
    blockAccordionContent: AccordionContent,
    ctaSimple: CtaFullSimple,
    blockFeaturedCaseStudies: FeaturedCaseStudyList,
    blockIconBlocks: IconBlocks,
    blockImageWithPartialOverlay: BlockImageWithPartialOverlay,
    blockImage: BlockImage,
    blockIndustryBlocks: IndustryBlocks,
    blockLogoCarousel: BlockLogoCarousel,
    blockLogoGrid: ClientLogoGrid,
    blockNumberedBlocks: NumberedBlocks,
    blockPageSection: PageSection,
    blockRichText: TextRichCentered,
    blockScrolljackSection: ScrolljackSection,
    blockTechnologyBlocks: TechnologyBlocks,
    blockSlideshow: BlockSlideshow,
    blockStackedCards: StackedCards,
    blockTabbedContent: TabbedContent,
    blockClientTestimonial: ClientTestimonial,
    blockDarkCircleBackground: BlockDarkSection,
    blockPeopleList: PeopleList,
    form: Form,
    blockJobList: JobList,
    blockTestimonialSlideShow: BlockTestimonialSlideShow,
    blockGradientCta: BlockGradientCta,
};

export const BlockRenderer = (props: BlockRendererProps) => {
    const { block, mode = 'light', index, isNested = false, context = 'default', suppressFirstBlockTopMargin } = props;
    if (Array.isArray(block)) {
        return (
            <>
                {block.map((b, i) => (
                    <BlockRenderer
                        index={i}
                        key={`block-${b.sys.id}`}
                        block={b}
                        mode={mode}
                        context={context}
                        suppressFirstBlockTopMargin={suppressFirstBlockTopMargin}
                    />
                ))}
            </>
        );
    }

    const contentTypeId = get(block, 'sys.contentType.sys.id') as string;

    if (!contentTypeId) {
        console.warn('Block missing contentType:', JSON.stringify(block, null, 2));
        return null;
    }

    // https://stackoverflow.com/questions/57086672/element-implicitly-has-an-any-type-because-expression-of-type-string-cant-b
    const Component = ContentTypeMap[contentTypeId as keyof typeof ContentTypeMap];

    if (!Component) {
        console.warn(`${contentTypeId} is not handled by the renderer. Block:`, block);
        return null;
    }

    const { id } = block.sys;
    const componentProps = {
        ...block,
        ...(isTypeBlockAccordionContent(block) && { blockAccordionContent: block }),
        ...(isTypeCtaSimple(block) && { ctaSimple: block }),
        ...(isTypeBlockFeaturedCaseStudies(block) && { blockFeaturedCaseStudies: block }),
        ...(isTypeBlockIconBlocks(block) && { blockIconBlocks: block }),
        ...(isTypeBlockImageWithPartialOverlay(block) && {
            id: block.sys.id,
            overline: block.fields.overline,
            headerText: block.fields.headerText,
            paragraphText: block.fields.paragraphText,
            image: transformImageWrapperToGraphQL(block.fields.image),
            tuckUnderHero: !isNested && index === 0,
        }),
        ...(isTypeBlockIndustryBlocks(block) && { blockIndustryBlocks: block }),
        ...(isTypeBlockLogoCarousel(block) && { blockLogoCarousel: block }),
        ...(isTypeBlockLogoGrid(block) && { blockLogoGrid: block }),
        // temp until we convert all components to graphql
        ...(isTypeBlockNumberedBlocks(block) && {
            blocks: Array.isArray(block.fields.facts) ? block.fields.facts.map(transformFactToGraphQL) : [],
        }),
        ...(isTypeBlockRichText(block) && { blockRichText: block }),
        ...(isTypeBlockScrolljackSection(block) && { blockScrolljackSection: block }),
        ...(isTypeBlockTechnologyBlocks(block) && { blockTechnologyBlocks: block }),
        ...(isTypeBlockSlideshow(block) && { blockSlideshow: block }),
        ...(isTypeBlockStackedCards(block) && { blockStackedCards: block }),
        ...(isTypeBlockTabbedContent(block) && { blockTabbedContent: block }),
        ...(isTypeBlockClientTestimonial(block) && { blockSlideshow: block }),
        ...(isTypeBlockDarkCircleBackground(block) && { blockDarkCircleBackground: block }),
        ...(isTypeBlockPeopleList(block) && { blockPeopleList: block }),
        ...(isTypeBlockJobList(block) && { blockJobList: block }),
        ...(isTypeForm(block) && { form: block }),
        ...(isTypeBlockGradientCta(block) && { blockGradientCta: block }),
        parent: block.parent,
    };

    const anchorId = getBlockAnchorId(block, index, isNested);
    const wrapperProps = {
        'data-component': Component.name,
        'data-content-type': `${contentTypeId}`,
        ...(anchorId && { id: anchorId }),
    };

    const content = (
        <div {...wrapperProps}>
            <Component
                key={`${contentTypeId}-${id}`}
                mode={mode}
                context={context}
                isFirstBlock={suppressFirstBlockTopMargin && index === 0 && !isNested}
                {...componentProps}
            />
        </div>
    );

    // Don't wrap nested blocks, dark section blocks, or the first top-level
    // block (above-the-fold LCP shouldn't start hidden and fade in) in
    // FadeOnScroll.
    const shouldFade = !isNested && !isTypeBlockDarkCircleBackground(block) && index !== 0;

    return shouldFade ? <FadeOnScroll>{content}</FadeOnScroll> : content;
};

export default BlockRenderer;
